/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Center,
  CopyButton,
  Divider,
  Flex,
  Text,
  Textarea,
} from "@mantine/core";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import sortByReferenceIds from "@/utils/sortByReferenceIds";
import DetailTemplateSection from "../DetailTemplateSection";

interface TabTemplateProps {
  template: any;
  caseTemplateDetail: any;
  styles: any;
  caseDetail: any;
}

const TabPanelTemplate = ({
  caseDetail,
  caseTemplateDetail,
  styles,
  template,
}: TabTemplateProps) => {
  const [caseTemplate, setCaseTemplate] = useState(caseTemplateDetail);

  const getTemplateSectionText = (data: any) => {
    if (!data) return "";
    const { subsections, isSimple, textboxes } = data;
    let paragraph = "";

    if (isSimple) {
      if (textboxes?.items) {
        paragraph = textboxes.items
          .map((textbox: any) => {
            if (!textbox?.value) return "";
            const value = textbox.value.trim();
            return !value.endsWith(".")
              ? value + ". "
              : value + " ";
          })
          .join("");
      }
    } else {
      if (subsections?.items) {
        subsections.items.forEach(({ name: subsectionName, fields }: any) => {
          if (subsectionName) {
            paragraph += `${subsectionName}\n`;
          }
          if (fields?.items) {
            fields.items.forEach(({ name, value }: any) => {
              if (name && value) {
                const trimmedName = name.trim();
                const lastChar = trimmedName.charAt(trimmedName.length - 1);
                if (lastChar === ":" || lastChar === "-") {
                  paragraph += `${trimmedName} ${value}\n`;
                } else {
                  paragraph += `${trimmedName}: ${value}\n`;
                }
              }
            });
          }
          paragraph += `\n`;
        });
      }
    }
    return paragraph;
  };

  useEffect(() => {
    if (!caseTemplateDetail) {
      setCaseTemplate(null);
      return;
    }
    
    // Create a deep clone to avoid mutating the original data
    let clonedTemplateDetail;
    try {
      clonedTemplateDetail = JSON.parse(JSON.stringify(caseTemplateDetail));
    } catch (error) {
      console.error("Error cloning template data:", error);
      setCaseTemplate(caseTemplateDetail);
      return;
    }
    
    setCaseTemplate(clonedTemplateDetail);
    
    if (template?.sectionOrder && clonedTemplateDetail?.items && Array.isArray(clonedTemplateDetail.items)) {
      try {
        // Process each section with enhanced error handling
        clonedTemplateDetail.items.forEach((section: any, sectionIndex: number) => {
          if (!section || typeof section !== 'object') {
            console.warn(`Invalid section at index ${sectionIndex}:`, section);
            return;
          }

          if (section?.subsections?.items && Array.isArray(section.subsections.items)) {
            // Process subsections
            section.subsections.items.forEach((subsection: any, subsectionIndex: number) => {
              if (!subsection || typeof subsection !== 'object') {
                console.warn(`Invalid subsection at section ${sectionIndex}, subsection ${subsectionIndex}:`, subsection);
                return;
              }

              // Sort fields within subsection
              if (subsection?.fields?.items && Array.isArray(subsection.fields.items) && subsection.fields.items.length > 0) {
                try {
                  // Validate fields have required properties
                  const validFields = subsection.fields.items.filter((field: any) => 
                    field && typeof field === 'object' && field.id
                  );
                  
                  if (validFields.length > 0 && subsection?.fieldOrder) {
                    const lstFields = sortByReferenceIds(validFields, subsection.fieldOrder);
                    if (lstFields && Array.isArray(lstFields) && lstFields.length === validFields.length) {
                      subsection.fields.items = lstFields;
                    } else {
                      console.warn(`Field sorting returned invalid result for section ${sectionIndex}, subsection ${subsectionIndex}`);
                    }
                  }
                } catch (error) {
                  console.error(`Error sorting fields in section ${sectionIndex}, subsection ${subsectionIndex}:`, error);
                  // Keep original order on error
                }
              }
            });

            // Sort subsections within section
            try {
              // Validate subsections have required properties
              const validSubsections = section.subsections.items.filter((subsection: any) => 
                subsection && typeof subsection === 'object' && subsection.id
              );
              
              if (validSubsections.length > 0 && section?.subsectionOrder) {
                const lstSubSection = sortByReferenceIds(validSubsections, section.subsectionOrder);
                if (lstSubSection && Array.isArray(lstSubSection) && lstSubSection.length === validSubsections.length) {
                  section.subsections.items = lstSubSection;
                } else {
                  console.warn(`Subsection sorting returned invalid result for section ${sectionIndex}`);
                }
              }
            } catch (error) {
              console.error(`Error sorting subsections in section ${sectionIndex}:`, error);
              // Keep original order on error
            }
          }
        });

        // Sort sections at the top level
        try {
          // Validate sections have required properties
          const validSections = clonedTemplateDetail.items.filter((section: any) => 
            section && typeof section === 'object' && section.id
          );
          
          if (validSections.length > 0 && template.sectionOrder) {
            const lstSections = sortByReferenceIds(validSections, template.sectionOrder);
            if (lstSections && Array.isArray(lstSections) && lstSections.length === validSections.length) {
              clonedTemplateDetail.items = lstSections;
            } else {
              console.warn("Section sorting returned invalid result");
            }
          }
        } catch (error) {
          console.error("Error sorting sections:", error);
          // Keep original order on error
        }

        setCaseTemplate(clonedTemplateDetail);
      } catch (error) {
        console.error("Error processing template sections:", error);
        // Fallback to original data
        setCaseTemplate(caseTemplateDetail);
      }
    }
  }, [caseTemplateDetail, template]);

  return (
    <Box style={styles}>
      {caseDetail?.recordings &&
        (caseDetail.recordings?.items?.length > 0 ? (
          !caseTemplate?.items ? (
            <Center>
              <Text>
                Template has not been populated. Tap "Generate" to create your
                record!
              </Text>
            </Center>
          ) : null
        ) : (
          <Center>
            <Text>No existing recordings. Tap "Record" to create one!</Text>
          </Center>
        ))}
      {caseTemplate?.items && Array.isArray(caseTemplate.items) && caseTemplate.items.map((secsItem: any, index: number) => (
        <DetailTemplateSection title={secsItem?.name || "Untitled Section"} key={index}>
          {secsItem?.subsections?.items && Array.isArray(secsItem.subsections.items) && secsItem.subsections.items.map(
            (subSecItem: any, subIndex: number) => (
              <Box className="header-subsection" key={subIndex} p={8}>
                <Card bg={"rgb(198, 198, 200)"} radius={0} p={5}>
                  <Text fz={12} fw={500} tt={"uppercase"} p={10}>
                    {subSecItem?.name || "Untitled Subsection"}
                  </Text>
                </Card>
                {subSecItem?.fields?.items && Array.isArray(subSecItem.fields.items) && subSecItem.fields.items.map((field: any, fieldIndex: number) => (
                  <Flex key={fieldIndex} flex={1} justify={"space-between"} p={8}>
                    <Text flex={1} fz={14}>
                      {field?.name || ""}
                    </Text>
                    <Textarea
                      flex={1}
                      autosize
                      readOnly
                      variant="unstyled"
                      fz={14}
                      ta={"right"}
                      value={field?.value || ""}
                    />
                  </Flex>
                ))}
              </Box>
            ),
          )}
          {secsItem?.textboxes?.items && Array.isArray(secsItem.textboxes.items) && secsItem.textboxes.items.map((textbox: any, textboxIndex: number) => (
            <Flex key={textboxIndex} flex={1} p={8}>
              <Textarea
                flex={1}
                autosize
                readOnly
                variant="unstyled"
                fz={14}
                value={textbox?.value || ""}
              />
            </Flex>
          ))}
          <Divider my={"md"} />
          <Flex align={"center"} justify={"center"} mb={15}>
            <CopyButton value={getTemplateSectionText(secsItem)} timeout={2000}>
              {({ copied, copy }) => (
                <Button variant="subtle" onClick={copy}>
                  {copied ? (
                    <Flex>
                      <IconCheck size={20} />
                      <Text ml={5}>Copied</Text>
                    </Flex>
                  ) : (
                    <Flex>
                      <IconCopy size={20} />
                      <Text ml={5}>Copy text</Text>
                    </Flex>
                  )}
                </Button>
              )}
            </CopyButton>
          </Flex>
        </DetailTemplateSection>
      ))}
    </Box>
  );
};
export default TabPanelTemplate;
